import {RoadmapService} from './roadmap.service';
import {Injectable} from '@angular/core';
import {marker as _} from '@colsen1991/ngx-translate-extract-marker';
import {TranslateService} from '@ngx-translate/core';
import {ColDef} from 'ag-grid-enterprise';
import {BehaviorSubject, combineLatest, Observable, Subscription} from 'rxjs';
import {first, map, switchMap, tap} from 'rxjs/operators';
import {APIService, KPI} from '../api';
import {AssetsService} from './assets.service';
import {DocumentTypesService} from './document-types.service';
import {InitiativesService} from './initiatives.service';
import {InvestmentCategoriesService} from './investment-categories.service';
import {InvestmentReasonsService} from './investment-reasons.service';
import {PerimetersService} from './perimeters.service';
import {ReferenceDataService} from './reference-data.service';
import {idMapToArray, ScopeService} from './scope.service';
import {YearsService} from "./years.service";
import {ActivatedRoute} from "@angular/router";
import {InvestmentScenariiService} from "./investment-scenarii.service";
import {QuotePartService} from './quote-part.service';
import { ConfigService } from './config.service';

export interface ObservableMap<T> {
  [key: string]: Observable<T>;
}

/**
 * Contains available table types
 */
export type TableTypes = 'asset' | 'invest' | 'initiative' | 'project' | 'site';

/**
 * Contains the name of all columns we get the filter values
 */
export type TableFilterColumns =
  'asset_id'
  | 'asset'
  | 'asset_label'
  | 'asset_type'
  | 'category'
  | 'asset_type_object'
  | 'cluster'
  | 'confidence_on_feasibility'
  | 'energy_type'
  | 'emission_category'
  | 'floor'
  | 'initiative_id'
  | 'initiative_label'
  | 'initiative_tag'
  | 'initiative_type_1'
  | 'initiative_type_2'
  | 'initiative_type_3'
  | 'initiative_status'
  | 'initiative_start_year'
  | 'installation_year'
  | 'investment_label'
  | 'invest_id'
  | 'investment_status'
  | 'investment_type'
  | 'investment_year'
  | 'maintenance_code'
  | 'maintenance_evaluator_name'
  | 'maintenance_evaluation'
  | 'maintenance_contract'
  | 'maintenance_company'
  | 'maintenance_company_name'
  | 'maintenance_level'
  | 'maintenance_manager'
  | 'manufacturer_control_frequency'
  | 'regulatory_control_frequency'
  | 'regulatory_control_required'
  | 'last_regulatory_control_result'
  | 'current_reserves_count'
  | 'overdue_reserves_count'
  | 'uniformat_ii_code'
  | 'maintenance_alert'
  | 'compliance_alert'
  | 'owner'
  | 'ownership_type'
  | 'parent_asset'
  | 'perimeter'
  | 'perimeter_type'
  | 'priority'
  | 'projects'
  | 'project_id'
  | 'project_label'
  | 'project_type'
  | 'reference'
  | 'gtp_code'
  | 'refrigerant_type'
  | 'responsible'
  | 'site'
  | 'site_name'
  | 'super_category'
  | 'sub_category'
  | 'taxonomy_objective'
  | 'workflow_round'
  | 'zone'
  | 'zone_details'
  | 'zone_object'
  // Workaround for investment technical nomenclature
  | 'investment_super_category'
  | 'investment_category'
  | 'investment_sub_category'
  | 'investment_asset_type'
  | 'quote_part'
  | 'price_sheets_count'
  | 'investment_current_price'
  | 'investment_status'
  | 'investment_final_schedule_to'
  | 'investment_budget_origin'
  | 'investment_created_on'
  | 'quantity_unit'
  | 'theoretical_lifetime'
  | 'code'
  | 'fidji_act_short_label'
  | 'custom_code'
  | 'project_code'
  | 'total_alerts'
  | 'missing_data_count'
  | 'equipment_validated'
  | 'brand'
  | 'model'
  | 'power'
  | 'import_source'
  | 'import_reference'
  | 'imported_at'


export const FilterColumnToModel: { [key in TableFilterColumns]: string } = {
  asset_id: 'AssetId',
  asset_label: 'AssetLabel',
  asset: 'AssetLabel',
  asset_type: 'AssetType',
  asset_type_object: 'AssetType',
  category: 'Category',
  cluster: 'Cluster',
  confidence_on_feasibility: 'ConfidenceOnFeasibility',
  energy_type: 'EnergyType',
  emission_category: 'EmissionCategory',
  floor: 'AssetFloor',
  initiative_id: 'InitiativeId',
  initiative_label: 'InitiativeLabel',
  initiative_tag: 'InitiativeTag',
  initiative_type_1: 'InitiativeType1',
  initiative_type_2: 'InitiativeType2',
  initiative_type_3: 'initiative_type_3',
  initiative_status: 'InitiativeStatus',
  initiative_start_year: 'InitiativeStartYear',
  installation_year: 'InstallationYear',
  investment_label: 'InvestmentLabel',
  invest_id: 'InvestmentId',
  investment_type: 'InvestmentType',
  investment_year: 'InvestmentYear',
  owner: 'Owner',
  maintenance_code: 'MaintenanceCode',
  maintenance_evaluator_name: 'MaintenanceEvaluatorName',
  maintenance_evaluation: 'MaintenanceEvaluation',
  maintenance_contract: 'MaintenanceContract',
  maintenance_company: 'MaintenanceCompany',
  maintenance_company_name: 'MaintenanceCompanyName',
  maintenance_level: 'MaintenanceLevel',
  maintenance_manager: 'MaintenanceManager',
  manufacturer_control_frequency: 'ManufacturerControlFrequency',
  regulatory_control_frequency: 'RegulatoryControlFrequency',
  regulatory_control_required: 'RegulatoryControlRequired',
  last_regulatory_control_result: 'LastRegulatoryControlResult',
  current_reserves_count: 'CurrentReservesCount',
  overdue_reserves_count: 'OverdueReservesCount',
  uniformat_ii_code: 'UniformatIiCode',
  maintenance_alert: 'MaintenanceAlert',
  compliance_alert: 'ComplianceAlert',
  total_alerts: 'TotalAlerts',
  missing_data_count: 'MissingDataCount',
  ownership_type: 'OwnershipType',
  parent_asset: 'ParentAsset',
  perimeter: 'Perimeter',
  perimeter_type: 'PerimeterType',
  priority: 'Priority',
  projects: 'ProjectId',
  project_id: 'ProjectId',
  project_label: 'ProjectLabel',
  project_type: 'ProjectType',
  reference: 'InvestmentReference',
  gtp_code: 'GTPCodeCombined',
  refrigerant_type: 'RefrigerantType',
  responsible: 'Responsible',
  site: 'MultiPerimeter',
  site_name: 'MultiPerimeter',
  sub_category: 'SubCategory',
  super_category: 'SuperCategory',
  taxonomy_objective: 'TaxonomyObjective',
  workflow_round: 'WorkflowRound',
  zone: 'Zone',
  zone_details: 'ZoneDetails',
  zone_object: 'Zone',
  // Workaround for investment technical nomenclature
  investment_super_category: 'InvestmentSuperCategory',
  investment_category: 'InvestmentCategory',
  investment_sub_category: 'InvestmentSubCategory',
  investment_asset_type: 'InvestmentAssetType',
  quote_part: 'QuotePart',
  price_sheets_count: 'PriceSheetsCount',
  investment_current_price: 'InvestmentCurrentPrice',
  investment_status: 'InvestmentStatus',
  investment_final_schedule_to: 'InvestmentFinalScheduleTo',
  investment_budget_origin: 'InvestmentBudgetOrigin',
  investment_created_on: 'InvestmentCreatedOn',
  quantity_unit: 'QuantityUnit',
  theoretical_lifetime: 'TheoreticalLifetime',
  code: 'ActionCode',
  fidji_act_short_label: 'initiative_type_3',
  custom_code: 'CustomCode',
  project_code: 'ProjectCode',
  equipment_validated: 'EquipmentValidated',
  brand: 'Brand',
  model: 'Model',
  power: 'Power',
  import_source: 'import_source',
  import_reference: 'import_reference',
  imported_at: 'imported_at',
};

export const numberColumns: string[] = [
  'expected_lifetime',
  'remaining_lifetime_without_impact',
  'planned_investment_number',
  'unplanned_investment_number',
  'total_lifetime',
  'lifetime_vs_theorical',
  'end_of_life',
]

export const numberVariableColumns: string[] = [
  'sponsorships_',
  'invoicings_'
]

export const operatorsMap: { [operator: string]: string } = {
  '<': 'lessThan',
  '<=': 'lessThanOrEqual',
  '>': 'greaterThan',
  '>=': 'lgreaterThanOrEqual',
  '<<': 'inRange',
  '==': 'equals',
  '!=': 'notEqual',
};

export const reversedOperatorsMap: { [operator: string]: string } = {
  'lessThan': '<',
  'lessThanOrEqual': '<=',
  'greaterThan': '>',
  'lgreaterThanOrEqual': '>=',
  'inRange': '<<',
  'equals': '==',
  'notEqual': '!=',
};

@Injectable()
export class SpreadsheetColumnsService {

  constructor(
    protected documentTypesService: DocumentTypesService,
    protected investmentCategoriesService: InvestmentCategoriesService,
    protected investmentReasonsService: InvestmentReasonsService,
    protected scope: ScopeService,
    protected translate: TranslateService,
    protected api: APIService,
    protected perimetersService: PerimetersService,
    protected referenceDataService: ReferenceDataService,
    protected assetsService: AssetsService,
    protected yearsService: YearsService,
    protected route: ActivatedRoute,
    protected investmentScenariiService: InvestmentScenariiService,
    protected initiativesService: InitiativesService,
    protected roadmapService: RoadmapService,
    protected quotePartService: QuotePartService,
    protected config: ConfigService
  ) { }

  protected subscriptions: Subscription[] = [];

  public _deleteInvestFilters = new BehaviorSubject<boolean>(false);
  public deleteInvestFilters$ = this._deleteInvestFilters.asObservable();
  public _openInvestFilters = new BehaviorSubject<boolean>(false);
  public openInvestFilters$ = this._openInvestFilters.asObservable();

  public _deleteAssetFilters = new BehaviorSubject<boolean>(false);
  public deleteAssetFilters$ = this._deleteAssetFilters.asObservable();
  public _openAssetFilters = new BehaviorSubject<boolean>(false);
  public openAssetFilters$ = this._openAssetFilters.asObservable();

  public _deleteInitiativeFilters = new BehaviorSubject<boolean>(false);
  public deleteInitiativeFilters$ = this._deleteInitiativeFilters.asObservable();
  public _openInitiativeFilters = new BehaviorSubject<boolean>(false);
  public openInitiativeFilters$ = this._openInitiativeFilters.asObservable();

  public _deleteProjectFilters = new BehaviorSubject<boolean>(false);
  public deleteProjectFilters$ = this._deleteProjectFilters.asObservable();
  public _openProjectFilters = new BehaviorSubject<boolean>(false);
  public openProjectFilters$ = this._openProjectFilters.asObservable();

  public _openSiteFilters = new BehaviorSubject<boolean>(false);
  public openSiteFilters$ = this._openSiteFilters.asObservable();
  public _deleteSiteFilters = new BehaviorSubject<boolean>(false);
  public deleteSiteFilters$ = this._deleteSiteFilters.asObservable();

  public _openAssetTypesFilters = new BehaviorSubject<boolean>(false);
  public openAssetTypesFilters$ = this._openAssetTypesFilters.asObservable();
  public _deleteAssetTypesFilters = new BehaviorSubject<boolean>(false);
  public deleteAssetTypesFilters$ = this._deleteAssetTypesFilters.asObservable();

  protected refrigerantTypesMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected sitesMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected perimeters = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected perimetersMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected clustersMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected investmentStatusesMap = {};
  protected furtherInformationsMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected prioritiesMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected perimeterTypesMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected assetFloorsMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected investmentTypesMap = {};
  protected superCategoriesMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected categoriesMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected workflowRoundsMap = { 0: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected subCategoriesMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected assetTypesMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected initiativeStatusesMap = { null: '-' };
  protected initiativeTypesMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected initiativeTypesFriendlyNameMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected initiativeTypesLeafFriendlyMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected initiativeTypesShortMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected energyTypesMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected initiativeOwnersMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected taxonomyObjectivesMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected ownershipTypeMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected emissionCategoriesMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected projectsMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected budgetOriginsMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };
  protected budgetScopesMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };

  protected trueFalseMap = {
    true: this.translate.instant('values.true'),
    false: this.translate.instant('values.false'),
  };

  protected trueFalseNullMap = {
    true: this.translate.instant('values.true'),
    false: this.translate.instant('values.false'),
    null: this.translate.instant('pages.common.ag-grid.internal.blanks'),
  }

  protected trueFalseToBeDefined = {
    true: this.translate.instant('values.true'),
    false: this.translate.instant('values.false'),
    null: this.translate.instant('quote_part.unknown'),
  }

  public confidenceMap = {
    [KPI.Confidence.LOW]: this.translate.instant('asset.confidence_levels.low'),
    [KPI.Confidence.MEDIUM]: this.translate.instant('asset.confidence_levels.medium'),
    [KPI.Confidence.HIGH]: this.translate.instant('asset.confidence_levels.high'),
  };

  protected technicalEvaluationMap = {
    null: this.translate.instant('pages.common.ag-grid.internal.blanks'),
    0: this.translate.instant('core.utils.state-levels.critical'),
    1: this.translate.instant('core.utils.state-levels.deteriorated'),
    2: this.translate.instant('core.utils.state-levels.acceptable'),
    3: this.translate.instant('core.utils.state-levels.good'),
  };
  protected confidenceOnFeasibilityMap = { null: this.translate.instant('pages.common.ag-grid.internal.blanks') };

  protected assetLevelsMap = {
    0: this.assetsService.getAssetTypeLevelLabel(0),
    1: this.assetsService.getAssetTypeLevelLabel(1),
    2: this.assetsService.getAssetTypeLevelLabel(2),
    3: this.assetsService.getAssetTypeLevelLabel(3),
    4: this.assetsService.getAssetTypeLevelLabel(4),
  };

  /**
   * Formate an integer to currency value
   * @example
   * @param value = 12000
   * @returns "12 000 €"
   */
  public nullFormatter(params) {
    if (params.value === 0 || params.value === null || params.value === '' || params.value === undefined) {
      return '-';
    } else {
      return params.value;
    }
  }

  public dateFormatter(params) {
    if (!params.value) {
      return '-';
    } else {
      return params.context.datePipe.transform(params.value, 'dd MMM yy');
    }
  }

  public numberSignFormatter(params) {
    if (params.value === null || params.value === '') {
      return '-';
    } else if (params.value <= 0) {
      return params.value;
    } else if (params.value > 0) {
      return `+${params.value}`;
    }
  }

  public paybackFormatter(params) {
    if (params.value === 0 || params.value === null || params.value === '') {
      return '-';
    } else {
      return params.value
        + ' '
        + params.context.translate.instant(_('monthes'));
    }
  }

  public energySavingFormatter(params) {
    if (params.value === 0 || params.value === null || params.value === '' || isNaN(params.value)) {
      return '-';
    } else {
      return parseFloat(params.value)
        + ' '
        + params.context.translate.instant(_('kWh'));
    }
  }

  /**
   * Formate and sanitize a number
   * @example
   * @param value = 12000
   * @returns "12 000"
   */
  public numberFormatter(params) {
    if (params.value === 0 || params.value === null || isNaN(params.value)) {
      return '-';
    } else {
      const formattedNumber = new Intl.NumberFormat(
        params.context.i18n.getCulture(), { style: 'decimal' }
      ).format(Math.round(params.value));
      const sanitizedNumber = formattedNumber.replace(/[\s\u202F\u00A0]/g, ' ');
      return sanitizedNumber;
    }
  }

  /**
   * Formate and sanitize an integer to currency value
   * @example
   * @param value = 12000
   * @returns "12 000 €"
   */
  public priceFormatter(params) {
    if (params.value === 0 || params.value === null || isNaN(params.value)) {
      return '-';
    } else {
      const formattedNumber = new Intl.NumberFormat(
        params.context.i18n.getCulture(), { style: 'decimal' }
      ).format(Math.round(params.value));
      const sanitizedNumber = formattedNumber.replace(/[\s\u202F\u00A0]/g, ' ');
      return sanitizedNumber + ' ' + params.context.i18n.getCurrency();
    }
  }

  public priceFormatterWithDefault(params) {
    if (params.value == undefined || params.value === null || isNaN(params.value)) {
      params.value = 0;
    }
    return new Intl.NumberFormat(
      params.context.i18n.getCulture(), { style: 'decimal' }
    ).format(Math.round(params.value))
      + ' '
      + params.context.i18n.getCurrency();
  }

  /**
   * Formate an integer to currency value
   * @example
   * @param value = 12000
   * @returns "12 000 €"
   */
  public priceFormatter2dec(params) {
    if (params.value === 0 || params.value === null) {
      return '-';
    } else {
      return params.context.utilsService.formateNumber(
        params.context.utilsService.round2dec(params.value, 2)
      ) + ' '
        + params.context.i18n.getCurrency();
    }
  }

  public priceFormatterByFlooring(params) {
    if (params.value === 0 || params.value === null || isNaN(params.value)) {
      return '-';
    } else {
      return new Intl.NumberFormat(
        params.context.i18n.getCulture(), { style: 'decimal' }
      ).format(params.value)
        + ' '
        + params.context.i18n.getCurrency()
        + ' '
        + params.context.translate.instant(_('/ m2'));
    }
  }

  public flooringAreaFormatter(params) {
    if (params.value === 0 || params.value === null || isNaN(params.value)) {
      return '-';
    } else {
      return new Intl.NumberFormat(
        params.context.i18n.getCulture(), { style: 'decimal' }
      ).format(params.value)
        + ' '
        + params.context.translate.instant(_('m²'));
    }
  }

  public kwhFormatter(params) {
    if (params.value === 0 || params.value === null || isNaN(params.value)) {
      return '-';
    } else {
      return (
        new Intl.NumberFormat(params.context.i18n.getCulture(), {
          style: 'decimal',
        }).format(Math.abs(params.value)) + ' ' + params.context.translate.instant(_('pages.energy.kwh'))
      );
    }
  }

  public ghgFormatter(params) {
    if (params.value === 0 || params.value === null || isNaN(params.value)) {
      return '-';
    } else {
      return (
        new Intl.NumberFormat(params.context.i18n.getCulture(), {
          style: 'decimal',
        }).format(params.value) + ' ' + params.context.translate.instant(_('pages.energy.kg_CO2'))
      );
    }
  }

  public percentageFormatter(params) {
    if (params.value === 0 || params.value === null) {
      return '-';
    } else {
      return `${params.value} %`;
    }
  }

  public referenceFormatter(params) {
    if (!params.data) {
      return '-';
    }

    const referencePrefix = params.data.reference_prefix;
    const gtpCode = params.data.gtp_code;

    if (referencePrefix && gtpCode) {
      return `${referencePrefix}${gtpCode}`;
    }

    if (referencePrefix) {
      return referencePrefix;
    }

    if (gtpCode) {
      return gtpCode;
    }

    return params.data.reference || '-';
  }

  public setInvestmentRowNumber(params) {
    const visualRowIndex = params.node.rowIndex;
    // Convert nodeManager.allNodesMap to an array, for a simpler use
    const expandedGroupChildList = Object.keys(
      params.api.clientSideRowModel.nodeManager.allNodesMap,
    )
      .map((key) => params.api.clientSideRowModel.nodeManager.allNodesMap[key])
      .filter(
        (rowNode) =>
          rowNode.uiLevel === 1 &&
          rowNode.rowIndex &&
          !rowNode.group &&
          rowNode.parent.childrenAfterFilter.some(
            (child) => child.id === rowNode.id,
          ),
      )
      .map((rowNode) => rowNode.rowIndex + 1);

    if (
      params.node.allChildrenCount ||
      (params.node.firstChild &&
        params.node.lastChild &&
        params.node.uiLevel === 0)
    ) {
      const childBefore = expandedGroupChildList.filter(
        (childIndex) => childIndex <= visualRowIndex,
      );
      return visualRowIndex - childBefore.length + 1;
    }
  }

  public setRowNumber(params) {
    return params?.node?.rowIndex + 1;
  }

  public technicalStateComparator(techState1, techState2) {
    const a = techState1.notation === null ? -1 : techState1.notation;
    const b = techState2.notation === null ? -1 : techState2.notation;
    if (techState1 && techState2) {
      return a - b;
    }
  }

  protected getAgGridImportColumns(): KPI.CustomColDef[] {
    return [
      {
        field: 'imported_at',
        headerName: this.translate.instant(
          'core.utils.spreadsheet_columns.column_imported_at',
        ),
        hide: true,
        filter: 'agDateColumnFilter',
        valueFormatter: (params) => {
          if (!params.value) return '-';
          const d = new Date(params.value);
          if (isNaN(d.getTime())) return '-';
          return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
        },
      },
      {
        field: 'import_source',
        headerName: this.translate.instant(
          'core.utils.spreadsheet_columns.column_import_source',
        ),
        hide: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: (params) => {
            if (params && params.colDef && params.colDef.field) {
              this.subscriptions.push(
                combineLatest([this.perimetersService.perimeters$, this.yearsService.startYear$, this.yearsService.endYear$, this.investmentScenariiService.mainScenario$])
                  .pipe(
                    switchMap(([perimeters, startYear, endYear, mainScenario]) => {
                      const fullFilterModel = params.api.getFilterModel() || {};
                      const currentColumnField = params.colDef.field;
                      const availableFilterField = params.colDef.availableFilterField;
                      const filterModelWithoutCurrentColumn = { ...fullFilterModel };
                      if (currentColumnField && filterModelWithoutCurrentColumn[currentColumnField]) {
                        delete filterModelWithoutCurrentColumn[currentColumnField];
                      }
                      if (availableFilterField && filterModelWithoutCurrentColumn[availableFilterField]) {
                        delete filterModelWithoutCurrentColumn[availableFilterField];
                      }
                      return this.api.available_filter_values({
                        model: 'import_source',
                        buildings: perimeters.reduce((acc: number[], p: any) => {
                          if (p.buildings) {
                            acc.push(...p.buildings.map((b: any) => b.id));
                          }
                          return acc;
                        }, []),
                        start_year: startYear,
                        end_year: endYear,
                        scenario: mainScenario.id,
                        filterModel: filterModelWithoutCurrentColumn,
                        table: 'Investment',
                      });
                    }),
                  )
                  .subscribe((res: any[]) => params.success([null, '', ...res])),
              );
            }
          },
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === '') {
              return this.translate.instant('pages.common.ag-grid.internal.blanks');
            }
            return params.value;
          },
        },
      },
      {
        field: 'import_reference',
        headerName: this.translate.instant(
          'core.utils.spreadsheet_columns.column_import_reference',
        ),
        hide: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: (params) => {
            if (params && params.colDef && params.colDef.field) {
              this.subscriptions.push(
                combineLatest([this.perimetersService.perimeters$, this.yearsService.startYear$, this.yearsService.endYear$, this.investmentScenariiService.mainScenario$])
                  .pipe(
                    switchMap(([perimeters, startYear, endYear, mainScenario]) => {
                      const fullFilterModel = params.api.getFilterModel() || {};
                      const currentColumnField = params.colDef.field;
                      const availableFilterField = params.colDef.availableFilterField;
                      const filterModelWithoutCurrentColumn = { ...fullFilterModel };
                      if (currentColumnField && filterModelWithoutCurrentColumn[currentColumnField]) {
                        delete filterModelWithoutCurrentColumn[currentColumnField];
                      }
                      if (availableFilterField && filterModelWithoutCurrentColumn[availableFilterField]) {
                        delete filterModelWithoutCurrentColumn[availableFilterField];
                      }
                      return this.api.available_filter_values({
                        model: 'import_reference',
                        buildings: perimeters.reduce((acc: number[], p: any) => {
                          if (p.buildings) {
                            acc.push(...p.buildings.map((b: any) => b.id));
                          }
                          return acc;
                        }, []),
                        start_year: startYear,
                        end_year: endYear,
                        scenario: mainScenario.id,
                        filterModel: filterModelWithoutCurrentColumn,
                        table: 'Investment',
                      });
                    }),
                  )
                  .subscribe((res: any[]) => params.success([null, '', ...res])),
              );
            }
          },
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === '') {
              return this.translate.instant('pages.common.ag-grid.internal.blanks');
            }
            return params.value;
          },
        },
      },
      // {
      //   field: 'import_statuses',
      //   headerName: this.translate.instant(
      //     'core.utils.spreadsheet_columns.column_import_statuses',
      //   ),
      //   hide: true,
      // },
    ];
  }

  protected getAgGridImportColumnGroup(): KPI.CustomColGroupDef {
    return {
      headerName: this.translate.instant('pages.common.ag-grid.column_groups.import'),
      icons: {
        columnGroupOpened: '<i class="far fa-minus"/>',
        columnGroupClosed: '<i class="far fa-plus"/>',
      },
      children: [
        {
          field: 'empty_import',
          headerName: '',
          width: 130,
          hide: true,
          lockVisible: true,
          columnGroupShow: 'closed',
          menuTabs: [],
          suppressColumnsToolPanel: true,
          suppressFiltersToolPanel: true,
          filter: false,
        } as KPI.CustomColDef,
        ...this.getAgGridImportColumns().map((col) => ({
          ...col,
          columnGroupShow: 'open' as const,
        })),
      ],
    };
  }

  protected buildAgGridInvestmentCategoriesColumns(hide: boolean = true): Observable<KPI.CustomColDef[]> {
    return this.investmentCategoriesService.investmentCategoriesOrdered$.pipe(
      first(),
      map((investmentCategories) => {
        const columns = [];
        // Build a map to retrieve
        const categoriesByLevels = investmentCategories.reduce(
          (catMap, item) => {
            const level = item.full_name.split(' > ').length;
            if (!catMap[level]) {
              catMap[level] = [];
            }
            catMap[level].push(item);
            return catMap;
          },
          {},
        );

        const levels = Object.keys(categoriesByLevels).map((item) => +item);
        const maxLevel = Math.max(...levels);
        for (let i = 1; i <= maxLevel; i++) {
          columns.push({
            field: `investment_category_${i}`,
            hide,
            columnGroupShow: 'open',
            headerName: i > 1
              ? this.translate.instant(
                'core.utils.spreadsheet_columns.column_budget_category_level',
                { level: i })
              : this.translate.instant(
                'core.utils.spreadsheet_columns.column_budget_category_level_1'
              )
            ,
            altName: this.translate.instant('core.utils.spreadsheet_columns.column_budget_category_level_1'),
          });
        }
        return columns;
      }),
    );
  }

  protected buildAgGridDocumentTypesColumns(hide: boolean = true): Observable<KPI.CustomColDef[]> {
    return this.documentTypesService.documentTypes$.pipe(
      first(),
      map((documentTypes) => {
        return documentTypes.reduce((columns, documentType) => {
          if (documentType.investment_type_requirements?.length) {
            columns.push({
              field: `document_${documentType.id}`,
              headerName: documentType.label,
              hide,
              width: 130,
              refData: this.trueFalseMap,
              columnGroupShow: 'open',
              filterParams: {
                values: (params) => {
                  params.success([true, false]);
                },
              },
            });
          }
          return columns;
        }, []);
      }),
    );
  }


  protected buildAgGridInvestmentReasonsColumns(hide: boolean = false): Observable<KPI.CustomColDef[]> {
    return this.investmentReasonsService.investmentReasons$.pipe(
      first(),
      map((investmentReasons) => {
        return investmentReasons.sort((a, b) => a.ordering - b.ordering).reduce(
          (columns, investmentReason) =>
            columns.concat([
              {
                field: `investment_reason_${investmentReason.id}`,
                headerName: investmentReason.name,
                headerClass: `investment-reason-${investmentReason.id}`,
                hide,
                minWidth: 150,
                maxWidth: 250,
                flex: 2,
                wrapHeaderText: false,
                cellStyle: params => {
                  if (params.value === true) {
                    return { color: '#00a000', fontWeight: 'bold' };
                  }
                },
                filterParams: {
                  values: (params) => {
                    params.success([true, false]);
                  },
                },
                cellRenderer: 'checkboxRenderer',
                refData: this.trueFalseMap,
              },
            ]),
          [],
        );
      }),
    );
  }

  protected buildAgGridReferenceFieldColumns(hide: boolean = true): Observable<KPI.CustomColDef[]> {
    return this.scope.referenceFields$.pipe(
      first(),
      idMapToArray(),
      map((referenceFields) => {
        return referenceFields.reduce(
          (columns, referenceField) =>
            referenceField.displayed_intelligence
              ? columns.concat([
                {
                  field: `reference_field_${referenceField.id}`,
                  hide,
                  headerName: referenceField.label,
                  aggFunc: 'first',
                },
              ])
              : columns,
          [],
        );
      }),
    );
  }

  protected buildAgGridDetailOfPayerShare(): Observable<KPI.CustomColGroupDef[]> {
    return combineLatest([
      this.scope.investmentSponsors$.pipe(idMapToArray()),
      this.quotePartService.quoteparts$.pipe(idMapToArray())
    ]).pipe(
      map(([sponsors, quoteParts]) => {
        const mapped = (quoteParts || []).reduce((acc, quotepart) => ({...acc, [quotepart.id]: quotepart.name}), this.confidenceOnFeasibilityMap);
      return [
        {
          headerName: this.translate.instant('pages.insight.quotepart.key'),
          icons: {
            columnGroupOpened: '<i class="far fa-minus"/>',
            columnGroupClosed: '<i class="far fa-plus"/>',
          },
          children: [
            {
              field: 'empty_key',
              headerName: '',
              width: 150,
              hide: true,
              lockVisible: true,
              columnGroupShow: 'closed',
              menuTabs: [],
              suppressColumnsToolPanel: true,
              suppressFiltersToolPanel: true,
              filter: false,
            },
            {
              headerName: this.translate.instant('pages.insight.quotepart.key'),
              field: 'quote_part',
              filter: 'agSetColumnFilter',
              valueGetter: (params) => params.data?.quote_part?.id ?? '-',
              valueFormatter: params => params.data?.quote_part?.name ?? "-",
              columnGroupShow: 'open',
              refData: mapped
            }
          ]
        },
        {
          headerName: this.translate.instant('pages.insight.quotepart.name'),
          headerClass: 'justify-center',
          hide: false,
          icons: {
            columnGroupOpened: '<i class="far fa-minus"/>',
            columnGroupClosed: '<i class="far fa-plus"/>',
          },
          children: [
            {
              field: 'empty_quote_part',
              headerName: '',
              width: 190,
              hide: true,
              lockVisible: true,
              columnGroupShow: 'closed',
              menuTabs: [],
              suppressColumnsToolPanel: true,
              suppressFiltersToolPanel: true,
              filter: false,
            },
            ...sponsors.reduce(
              (columns, sponsor) =>
                columns.concat([
                  {
                    headerName: sponsor.name,
                    field: `sponsorships_${sponsor.id}`,
                    headerClass: 'text-center',
                    valueFormatter: this.priceFormatterWithDefault,
                    hide: false,
                    width: 150,
                    columnGroupShow: 'open',
                    filter: 'agNumberColumnFilter',
                  },
                ]),
              [],
            ) as KPI.CustomColDef[]
          ],
        },
      ]
    }));
  }

  protected buildAgGridRoadmapIndicatorColumns(hide: boolean = true): Observable<KPI.CustomColDef[]> {
    return this.roadmapService.roadmapIndicators().pipe(
      first(),
      map((roadmapIndicators) => {
        return roadmapIndicators.reduce(
          (columns, roadmapIndicator) =>
            columns.concat([
              {
                field: `impact_roadmap_indicators_${roadmapIndicator.id}`,
                headerName: roadmapIndicator.name,
                hide,
                width: 150,
                cellStyle: params => {
                  if (params.value === true) {
                    return { color: '#00a000', fontWeight: 'bold' };
                  }
                },
                filterParams: {
                  values: (params) => {
                    params.success([true, false]);
                  },
                },
                cellRenderer: 'checkboxRenderer',
                refData: this.trueFalseMap,
                columnGroupShow: 'open',
              },
            ]),
          [],
        );
      }),
    );
  }

  private generatePayerColumns(
    list: KPI.Sponsor[],
    type: 'sponsorships' | 'invoicings',
    hide: boolean = true,
  ): KPI.CustomColDef[] {
    return list.map((item) => ({
      field: `${type}_${item.id}`,
      headerName: `${item.name}`,
      hide,
      width: 170,
      valueFormatter: this.priceFormatter,
      filter: 'agNumberColumnFilter',
      quickFilter: item.display_in_spreadsheet_filters,
      booleanToNumberFilter: true,
    }));
  };

  protected buildAgGridPayersColumns(hide: boolean = true): Observable<KPI.CustomColDef[]> {
    return combineLatest([
      this.perimetersService.perimeters$,
      this.scope.investmentSponsors$.pipe(idMapToArray()),
    ]).pipe(
      map(([perimeters, investmentSponsors]) =>
        investmentSponsors.filter((investmentSponsor) =>
          investmentSponsor.perimeters.some((perimeter) => perimeters.some((p) => p.id === perimeter))
        ),
      ),
      map((investmentSponsors) => {
        return this.generatePayerColumns(investmentSponsors, 'sponsorships', hide);
      }),
    );
  }

  protected buildAgGridInvoicingsColumns(hide: boolean = true): Observable<KPI.CustomColDef[]> {
    return combineLatest([
      this.perimetersService.perimeters$,
      this.scope.investmentInvoicesRecipient$.pipe(idMapToArray()),
    ]).pipe(
      map(([perimeters, investmentInvoicesRecipient]) =>
        investmentInvoicesRecipient.filter((invoicesRecipient) =>
          invoicesRecipient.perimeters.some((perimeter) => perimeters.some((p) => p.id === perimeter))
        ),
      ),
      map((investmentInvoicesRecipient) => {
        return this.generatePayerColumns(investmentInvoicesRecipient, 'invoicings', hide);
      }),
    );
  }

  public getAgGridSiteProvidersColumns(): Observable<ColDef[]> {
    return this.investmentScenariiService.scenarii$.pipe(
      map((scenarii) =>
        [
          {
            field: 'name',
            headerName: this.translate.instant(
              'core.utils.spreadsheet_columns.column_site',
            ),
            sort: 'asc',
          },
          {
            field: 'cluster',
            headerName: this.translate.instant(
              'core.utils.spreadsheet_columns.column_cluster',
            ),
          },
          ...scenarii.map((scenario) => ({
            field: `provider_scenario_${scenario.id}`,
            headerClass: `scenario-${scenario.id}`,
            headerName: scenario.name,
          })),
        ],
      ),
    );
  }

  public getAgGridCodesColumn(): Observable<ColDef[]> {
    return combineLatest([
      this.config.customerConfig$,
      this.scope.investmentStatuses$.pipe(first())
    ]).pipe(
      map(([customerConfig, investmentStatuses]) => {
        const showCustomCode = !!customerConfig.show_investment_custom_code;
        const showProjectCode = !!customerConfig.show_investment_project_code;
        const showGTPCode = !!customerConfig.show_investment_gtp_code;
        const customCodeLabel = customerConfig.investment_code_field_name
          ? customerConfig.investment_code_field_name
          : this.translate.instant('Customize code');
        const children: ColDef[] = [];
        if (showCustomCode) {
          children.push({
            hide: true,
            field: 'custom_code',
            headerName: customCodeLabel,
          });
        }
        if (showProjectCode) {
          children.push({
            hide: true,
            field: 'project_code',
            headerName: this.translate.instant('Project code'),
          });
        }
        if (showGTPCode) {
          children.push({
            hide: true,
            field: 'gtp_code',
            headerName: this.translate.instant('GTP code'),
            valueFormatter: (params) => {
              const statusId = params.data?.status_scenario_1 ||
                              (params.data?.scenarii_data && params.data.scenarii_data.length > 0 ? params.data.scenarii_data[0].status : null);
              if (!statusId) {
                return '';
              }
              const statusObject = investmentStatuses[statusId];
              if (!statusObject) {
                return '';
              }
              const isExcluded = statusObject.hypothesis === true ||
                                statusObject.is_request === true ||
                                statusObject.is_proposed === true ||
                                statusObject.is_cancelled === true;
              if (isExcluded) {
                return '';
              }
              return this.referenceFormatter(params);
            },
            filter: 'agTextColumnFilter',
            sortable: false,
          });
        }
        return children;
      }),
    );
  }

  public getAgGridPostponementColumns(): Promise<ColDef[]> {
    return new Promise((resolve) => {
      const columns = [
        {
          field: 'label',
          headerName: this.translate.instant(
            'core.utils.spreadsheet_columns.column_label',
          ),
          hide: false,
        },
        {
          field: 'site',
          headerName: this.translate.instant('Site'),
          hide: false,
        },
        {
          field: 'current_price',
          headerName: this.translate.instant(
            'core.utils.spreadsheet_columns.column_budget',
          ),
          hide: false,
          valueFormatter: this.priceFormatter,
        },
        {
          field: 'final_schedule_to',
          headerName: this.translate.instant(
            'core.utils.spreadsheet_columns.column_year',
          ),
          hide: false,
        },
        {
          field: 'status',
          headerName: this.translate.instant(
            'core.utils.spreadsheet_columns.column_investment_status',
          ),
          hide: false,
          // refData: this.investmentStatusesMap,
        },
        {
          field: 'asset',
          headerName: this.translate.instant('Equipment'),
          hide: false,
          valueGetter: (val) => val.data?.asset?.label ?? '',
        },
        {
          field: 'super_category',
          headerName: this.translate.instant('Super category'),
          hide: false,
        },
        {
          field: "category",
          headerName: this.translate.instant('Category'),
          hide: false,
        },
        {
          field: "sub_category",
          headerName: this.translate.instant('Sub category'),
          hide: false,
        },
        {
          field: "postponement_reason",
          headerName: this.translate.instant('Motive'),
          hide: false,
        },
      ];
      resolve(columns);
    });
  }

  public getAgGridTranslations(): Observable<any> {
    return this.translate.get([
      'pages.common.ag-grid.internal.select_all',
      'pages.common.ag-grid.internal.select_all_search_results',
      'pages.common.ag-grid.internal.search_ooo',
      'pages.common.ag-grid.internal.blanks',
      'pages.common.ag-grid.internal.no_matches',
      'pages.common.ag-grid.internal.filter_ooo',
      'pages.common.ag-grid.internal.equals',
      'pages.common.ag-grid.internal.not_equal',
      'pages.common.ag-grid.internal.empty',
      'pages.common.ag-grid.internal.before',
      'pages.common.ag-grid.internal.after',
      'pages.common.ag-grid.internal.less_than',
      'pages.common.ag-grid.internal.greater_than',
      'pages.common.ag-grid.internal.less_than_or_equal',
      'pages.common.ag-grid.internal.greater_than_or_equal',
      'pages.common.ag-grid.internal.in_range',
      'pages.common.ag-grid.internal.in_range_start',
      'pages.common.ag-grid.internal.in_range_end',
      'pages.common.ag-grid.internal.contains',
      'pages.common.ag-grid.internal.not_contains',
      'pages.common.ag-grid.internal.starts_with',
      'pages.common.ag-grid.internal.ends_with',
      'pages.common.ag-grid.internal.and_condition',
      'pages.common.ag-grid.internal.or_condition',
      'pages.common.ag-grid.internal.apply_filter',
      'pages.common.ag-grid.internal.reset_filter',
      'pages.common.ag-grid.internal.clear_filter',
      'pages.common.ag-grid.internal.cancel_filter',
      'pages.common.ag-grid.internal.text_filter',
      'pages.common.ag-grid.internal.number_filter',
      'pages.common.ag-grid.internal.date_filter',
      'pages.common.ag-grid.internal.set_filter',
      'pages.common.ag-grid.internal.columns',
      'pages.common.ag-grid.internal.filters',
      'pages.common.ag-grid.internal.groups',
      'pages.common.ag-grid.internal.group',
      'pages.common.ag-grid.internal.no_rows_to_show',
      'pages.common.ag-grid.internal.pin_column',
      'pages.common.ag-grid.internal.pin_left',
      'pages.common.ag-grid.internal.pin_right',
      'pages.common.ag-grid.internal.no_pin',
      'pages.common.ag-grid.internal.value_aggregation',
      'pages.common.ag-grid.internal.autosize_this_column',
      'pages.common.ag-grid.internal.autosize_all_columns',
      'pages.common.ag-grid.internal.group_by',
      'pages.common.ag-grid.internal.ungroup_by',
      'pages.common.ag-grid.internal.reset_columns',
      'pages.common.ag-grid.internal.choose_columns',
      'pages.common.ag-grid.internal.sort_ascending',
      'pages.common.ag-grid.internal.sort_descending',
      'pages.common.ag-grid.internal.sort_unsort',
      'pages.common.ag-grid.internal.expand_all',
      'pages.common.ag-grid.internal.collapse_all',
      'pages.common.ag-grid.internal.blank',
      'pages.common.ag-grid.internal.not_blank',
      'pages.common.ag-grid.internal.to',
      'pages.common.ag-grid.internal.page',
      'pages.common.ag-grid.internal.of',
    ]).pipe(
      map((trans) => {
        return {
          selectAll: trans['pages.common.ag-grid.internal.select_all'],
          selectAllSearchResults: trans['pages.common.ag-grid.internal.select_all_search_results'],
          searchOoo: trans['pages.common.ag-grid.internal.search_ooo'],
          blanks: trans['pages.common.ag-grid.internal.blanks'],
          noMatches: trans['pages.common.ag-grid.internal.no_matches'],
          filterOoo: trans['pages.common.ag-grid.internal.filter_ooo'],
          equals: trans['pages.common.ag-grid.internal.equals'],
          notEqual: trans['pages.common.ag-grid.internal.not_equal'],
          empty: trans['pages.common.ag-grid.internal.empty'],
          before: trans['pages.common.ag-grid.internal.before'],
          after: trans['pages.common.ag-grid.internal.after'],
          lessThan: trans['pages.common.ag-grid.internal.less_than'],
          greaterThan: trans['pages.common.ag-grid.internal.greater_than'],
          lessThanOrEqual:
            trans['pages.common.ag-grid.internal.less_than_or_equal'],
          greaterThanOrEqual:
            trans['pages.common.ag-grid.internal.greater_than_or_equal'],
          inRange: trans['pages.common.ag-grid.internal.in_range'],
          inRangeStart: trans['pages.common.ag-grid.internal.in_range_start'],
          inRangeEnd: trans['pages.common.ag-grid.internal.in_range_end'],
          contains: trans['pages.common.ag-grid.internal.contains'],
          notContains: trans['pages.common.ag-grid.internal.not_contains'],
          startsWith: trans['pages.common.ag-grid.internal.starts_with'],
          endsWith: trans['pages.common.ag-grid.internal.ends_with'],
          andCondition: trans['pages.common.ag-grid.internal.and_condition'],
          orCondition: trans['pages.common.ag-grid.internal.or_condition'],
          applyFilter: trans['pages.common.ag-grid.internal.apply_filter'],
          resetFilter: trans['pages.common.ag-grid.internal.reset_filter'],
          clearFilter: trans['pages.common.ag-grid.internal.clear_filter'],
          cancelFilter: trans['pages.common.ag-grid.internal.cancel_filter'],
          textFilter: trans['pages.common.ag-grid.internal.text_filter'],
          numberFilter: trans['pages.common.ag-grid.internal.number_filter'],
          dateFilter: trans['pages.common.ag-grid.internal.date_filter'],
          setFilter: trans['pages.common.ag-grid.internal.set_filter'],
          columns: trans['pages.common.ag-grid.internal.columns'],
          filters: trans['pages.common.ag-grid.internal.filters'],
          groups: trans['pages.common.ag-grid.internal.groups'],
          group: trans['pages.common.ag-grid.internal.group'],
          noRowsToShow:
            trans['pages.common.ag-grid.internal.no_rows_to_show'],
          pinColumn: trans['pages.common.ag-grid.internal.pin_column'],
          pinLeft: trans['pages.common.ag-grid.internal.pin_left'],
          pinRight: trans['pages.common.ag-grid.internal.pin_right'],
          noPin: trans['pages.common.ag-grid.internal.no_pin'],
          valueAggregation:
            trans['pages.common.ag-grid.internal.value_aggregation'],
          autosizeThisColumn:
            trans['pages.common.ag-grid.internal.autosize_this_column'],
          autosizeAllColumns:
            trans['pages.common.ag-grid.internal.autosize_all_columns'],
          groupBy: trans['pages.common.ag-grid.internal.group_by'],
          ungroupBy: trans['pages.common.ag-grid.internal.ungroup_by'],
          resetColumns: trans['pages.common.ag-grid.internal.reset_columns'],
          columnChooser: trans['pages.common.ag-grid.internal.choose_columns'],
          sortAscending: trans['pages.common.ag-grid.internal.sort_ascending'],
          sortDescending: trans['pages.common.ag-grid.internal.sort_descending'],
          sortUnSort: trans['pages.common.ag-grid.internal.sort_unsort'],
          expandAll: trans['pages.common.ag-grid.internal.expand_all'],
          collapseAll: trans['pages.common.ag-grid.internal.collapse_all'],
          blank: trans['pages.common.ag-grid.internal.blank'],
          notBlank: trans['pages.common.ag-grid.internal.not_blank'],
          to: trans['pages.common.ag-grid.internal.to'],
          page: trans['pages.common.ag-grid.internal.page'],
          of: trans['pages.common.ag-grid.internal.of'],
        };
      }),
    );
  }

  protected mapColumnWithLocalStorage(column, savedColumns) {
    const columnsToIgnore = [
      'year_scenario_',
      'status_scenario_',
    ]
    const savedColumn = savedColumns.find(
      (savedCol) => savedCol.field === column.field,
    );
    const shouldIgnoreCol = columnsToIgnore.find((col) => savedColumn?.field.includes(col));
    if (savedColumn && !shouldIgnoreCol) {
      return {
        ...column,
        ...savedColumn,
        hide: !['investment_id', 'row_nb'].includes(column.field)
          ? savedColumn
            ? savedColumn.hide
            : column.hide
          : column.hide,
      };
    }
    // If is a child column group
    if (column.children) {
      return {
        ...column,
        ...savedColumn,
        children: column.children
          ? column.children.map((columnChild) => {
            // Child column level
            const savedColumChild = savedColumns.find(
              (savedCol) => savedCol.field === columnChild.field,
            );
            const shouldIgnoreChild = columnsToIgnore.find((col) => savedColumn?.field.includes(col));
            if (savedColumChild && !shouldIgnoreChild) {
              return {
                ...columnChild,
                ...savedColumChild,
                hide: savedColumChild
                  ? savedColumChild.hide
                  : columnChild.hide,
              };
            }
            return columnChild;
          })
          : [],
      };
    }
    return column;
  }

  protected loadSiteAndPerimeters(): Observable<KPI.Perimeter[]> {
    return this.perimetersService.allPerimeters$.pipe(
      tap((perimeters: KPI.Perimeter[]) =>
        perimeters.forEach((perimeter) => {
          this.sitesMap[perimeter.id] = perimeter.name;
          perimeter.sub_perimeters.forEach((sub_perimeter) => {
            const levelParent = sub_perimeter.level_parent
              ? perimeter.sub_perimeters.find((parent) => parent.id === sub_perimeter.level_parent)
              : null;
            this.perimetersMap[sub_perimeter.id] = sub_perimeter.name
              + (levelParent ? ` - ${levelParent.name} - ` : ' - ')
              + perimeter.name;
            this.perimeters[sub_perimeter.id] = sub_perimeter.name;
          });
        })
      ),
    );
  }

}
